import { useState, useEffect, useRef, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import DynaLoader from '../components/DynaLoader';

import ether from '../images/ethereum.svg';

import { getContract, getContractWithSigner, isCurrentAccountOwner, fetchFormattedMintPrice, setMintPriceOnContract, makePaymentFromContract, fetchContractBalance } from '../utils/BlockchainAPI';
import { handleError } from '../utils/ErrorHandler';

import { generateRandomStripesDataUri } from '../utils/DynaStripes';

import DynaSpan from '../components/DynaSpan';

const AdminPage = () => {
  const mintPriceInput = useRef(null);
  const tokenLimitInput = useRef(null);
  const paymentInput = useRef(null);
  const ownerInput = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSenderOwner, setIsSenderOwner] = useState(false);
  const [ownerAddress, setOwnerAddress] = useState(null);
  const [mintPrice, setMintPrice] = useState(null);
  const [contractBalance, setContractBalance] = useState(null);
  const [tokenLimit, setTokenLimit] = useState(null);


  const fetchMintPriceVal = useCallback(async () => {
    setMintPrice("-");
    try {
      const price = await fetchFormattedMintPrice();
      console.log("Got formatted price: " + price);

      setMintPrice(price);
    } catch (err) {
      handleError(err);
    }
  }, []);

  const fetchTokenLimit = useCallback(async () => {
    try {
      const contract = await getContract();

      setTokenLimit("-");

      const limit = await contract.getTokenLimit();
      setTokenLimit(limit);
    } catch (err) {
      handleError(err);
    }
  }, []);

  const fetchOwner = useCallback(async () => {
    try {
      const contract = await getContract();
      const owner = await contract.owner();
      console.log("Owner: " + owner);
      setOwnerAddress(owner);

    } catch (err) {
      handleError(err);
      setOwnerAddress("?");
    }
  }, []);

  const getContractBalance = useCallback(async () => {
    try {
      setContractBalance("-");
      const formattedBalance = await fetchContractBalance();
      console.log("Contract balance: " + formattedBalance);
      setContractBalance(formattedBalance);
    } catch (err) {
      handleError(err);
    }
  }, []);

  useEffect(() => {
    const fetchOwnerStatus = async () => {
      console.log("Fetching owner status..");
      try {
        const isSenderOwner = await isCurrentAccountOwner();

        if (isSenderOwner === true) {
          toast("You're the owner. You must have built me! You're awesome. 😘");
          // We can call these concurrently or sequentially
          await Promise.all([
            fetchMintPriceVal(),
            fetchTokenLimit(),
            fetchOwner(),
            getContractBalance()
          ]);
        } else {
          toast("You're not the owner. Life must be painful and dull. And this page is definitely not for you.");
        }

        setIsLoading(false);
        setIsSenderOwner(isSenderOwner);

      } catch (err) {
        handleError(err);
        setIsLoading(false);
        setIsSenderOwner(false);
      }
    }

    fetchOwnerStatus();
  }, [fetchMintPriceVal, fetchTokenLimit, fetchOwner, getContractBalance]);

  const pauseContract = async () => {
    try {
      const contractWithSigner = await getContractWithSigner();
      await contractWithSigner.pause();
      console.log('Contract paused.');
      toast.success('Contract paused.');
    } catch (err) {
      handleError(err);
    }
  }

  const unpauseContract = async () => {
    try {
      const contractWithSigner = await getContractWithSigner();
      await contractWithSigner.unpause();
      console.log('Contract unpaused.');
      toast.success('Contract unpaused.');
    } catch (err) {
      handleError(err);
    }
  }

  // Renamed to avoid infinite recursion / naming conflict with imported function

  const updateMintPrice = async () => {
    const newMintPrice = mintPriceInput.current.value;
    console.log("Updating price: " + newMintPrice);


    try {
      setMintPriceOnContract(newMintPrice);
      setMintPrice(newMintPrice);

      toast.success('Mint price set to: ' + newMintPrice);

    } catch (err) {
      handleError(err);
    }
  }


  const updateTokenLimit = async () => {
    const newTokenLimit = tokenLimitInput.current.value;
    console.log("Updating token limit: " + newTokenLimit);

    try {
      const contractWithSigner = await getContractWithSigner();
      await contractWithSigner.setTokenLimit(newTokenLimit);

      setTokenLimit(newTokenLimit);

      toast.success('Token limit set to: ' + newTokenLimit);
      fetchTokenLimit();

    } catch (err) {
      handleError(err);
    }
  }

  const makePayment = async () => {
    const amount = paymentInput.current.value;
    console.log("Making payment (MATIC): " + amount);

    try {
      await makePaymentFromContract(amount);
      toast.success('Made payment of ' + amount + " MATIC");

    } catch (err) {
      handleError(err);
    }
  }


  const updateOwner = async () => {
    const newOwner = ownerInput.current.value;
    console.log("Setting owner to: " + newOwner);

    try {
      const contractWithSigner = await getContractWithSigner();
      await contractWithSigner.transferOwnership(newOwner);

      toast.success('Owner is now: ' + newOwner);

    } catch (err) {
      handleError(err);
    }
  }


  const svgDataUri = generateRandomStripesDataUri();

  if (isLoading) {
    return (
      <div className="mainContent" style={{ background: svgDataUri }}>
        <div className="content">
          <h1>Loading..</h1>
          <div className="deepContent">
            <center>
              <DynaLoader variant="block" />
            </center>
          </div>
        </div>

      </div>
    );

  } else if (!isSenderOwner) {
    return (
      <div className="mainContent" style={{ background: svgDataUri }}>
        <div className="content">
          <h1>Hmmmmm...</h1>
          <div className="deepContent">
            <p>
              Just what is it that you think you're doing here? 🤨
            </p>
          </div>

        </div>

      </div>
    );
  } else {
    return (

      <div className="mainContent" style={{ background: svgDataUri }}>
        <div className="content">
          <h1><DynaSpan /> Admin Settings</h1>
          <div className="deepContent">
            <center>
              Contract status: ? <br />
              <Button variant="primary" onClick={pauseContract}>Pause</Button>
              <Button variant="primary" onClick={unpauseContract}>Unpause</Button>
              <br /><hr />
              Current mint price: {mintPrice === null ? "-" : mintPrice}<img src={ether} alt="ether logo" className='mintEther' /> <br />
              <input ref={mintPriceInput} /><br />
              <Button variant="primary" onClick={updateMintPrice}>Update mint price</Button>
              <br /><hr />
              Current token limit (max 10,000): {tokenLimit === null ? "-" : tokenLimit}<br />
              <input ref={tokenLimitInput} /><br />
              <Button variant="primary" onClick={updateTokenLimit}>Update token Limit</Button>
              <br /><hr />
              Make payment. Current balance: {contractBalance} <br />
              <input ref={paymentInput} /><br />
              <Button variant="primary" onClick={makePayment}>Pay owner</Button>
              <br /><hr />
              Current owner ETH address:<br /> {ownerAddress} <br />
              <input ref={ownerInput} /><br />
              <Button variant="primary" onClick={updateOwner}>Update owner</Button>
            </center>

          </div>
        </div>
      </div>
    );
  }
}

export default AdminPage;